import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

const getDisplayLabel = (option, labelKey) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return labelKey && option[labelKey] != null ? String(option[labelKey]) : String(option);
};

const getOptionKey = (option, index) => {
  if (option && option.id !== undefined) return `opt-${option.id}`;
  if (option && option.value !== undefined) return `opt-${option.value}`;
  return `opt-${index}`;
};

const useSearchableDropdown = ({ value, options, getLabel, onSelect, searchable = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const term = search.trim().toLowerCase();
    return term ? options.filter((o) => getLabel(o).toLowerCase().includes(term)) : options;
  }, [options, search, getLabel, searchable]);

  const selectedIndex = useMemo(() => {
    const selectedLabel = getLabel(value);
    return filtered.findIndex((o) => getLabel(o) === selectedLabel);
  }, [filtered, value, getLabel]);

  useEffect(() => {
    setSearch(value ? getLabel(value) : '');
    setActiveIndex(-1);
  }, [value, getLabel]);

  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  const handleSelect = useCallback((option) => {
    setSearch(getLabel(option));
    setIsOpen(false);
    setActiveIndex(-1);
    onSelect(option);
  }, [getLabel, onSelect]);

  const handleClear = useCallback((e) => {
    e.preventDefault();
    setSearch('');
    setIsOpen(false);
    setActiveIndex(-1);
    onSelect(null);
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const index = activeIndex >= 0 ? activeIndex : 0;
      if (filtered.length > index) handleSelect(filtered[index]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, [filtered, activeIndex, handleSelect]);

  return {
    search,
    setSearch,
    isOpen,
    setIsOpen,
    activeIndex,
    filtered,
    handleSelect,
    handleClear,
    handleKeyDown
  };
};

const DropdownList = ({ options, getLabel, activeIndex, selectedValue, onSelect, emptyText }) => {
  const selectedLabel = getLabel(selectedValue);
  return (
    <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg">
      {options.length === 0 ? (
        <li className="px-3 py-2 text-sm text-gray-500">{emptyText || 'No matches'}</li>
      ) : (
        options.map((option, index) => {
          const isActive = index === activeIndex;
          const isSelected = getLabel(option) === selectedLabel;
          return (
            <li
              key={getOptionKey(option, index)}
              onMouseDown={(e) => { e.preventDefault(); onSelect(option); }}
              className={`px-3 py-2 text-sm cursor-pointer ${isActive ? 'bg-purple-100 text-purple-800' : 'hover:bg-purple-50 text-gray-900'} ${isSelected ? 'font-medium' : ''}`}
            >
              {getLabel(option)}
            </li>
          );
        })
      )}
    </ul>
  );
};

const SearchableDropdown = ({ value, options, onSelect, labelKey, placeholder, disabled, emptyText, searchable = true }) => {
  const inputRef = useRef(null);
  const getLabel = useCallback((option) => getDisplayLabel(option, labelKey), [labelKey]);
  const {
    search,
    setSearch,
    isOpen,
    setIsOpen,
    activeIndex,
    filtered,
    handleSelect,
    handleClear,
    handleKeyDown
  } = useSearchableDropdown({ value, options, getLabel, onSelect, searchable });

  const selectedLabel = value ? getLabel(value) : placeholder || '';

  return (
    <div className="relative">
      {searchable ? (
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => !disabled && setIsOpen(true)}
          onClick={() => !disabled && setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full text-sm border border-gray-300 rounded-md pl-3 pr-10 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      ) : (
        <div
          role="combobox"
          aria-expanded={isOpen}
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setIsOpen(true)}
          onFocus={() => !disabled && setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={handleKeyDown}
          className="w-full text-sm border border-gray-300 rounded-md pl-3 pr-10 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer bg-white flex items-center select-none"
        >
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>{selectedLabel}</span>
        </div>
      )}
      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
        {searchable && search ? (
          <X size={16} className="cursor-pointer hover:text-gray-600" onMouseDown={handleClear} />
        ) : (
          <ChevronDown
            size={16}
            className="cursor-pointer hover:text-gray-600"
            onMouseDown={(e) => {
              e.preventDefault();
              !disabled && setIsOpen(!isOpen);
            }}
          />
        )}
      </div>
      {isOpen && !disabled && (
        <DropdownList
          options={filtered}
          getLabel={getLabel}
          activeIndex={activeIndex}
          selectedValue={value}
          onSelect={handleSelect}
          emptyText={emptyText}
        />
      )}
    </div>
  );
};

export default SearchableDropdown;
